import React, { useState } from 'react';
import { Badge, BlockStack, Button, Card, Divider, FormLayout, Layout, Select, Text, TextField } from "@shopify/polaris";
import CurrencyPreview from "../CurrencyPreview";
import { currencyListOptions } from "../../helper/currencyList";
import { countryList, countryNameFor } from "../../helper/countryList";
import { getSymbolOptions, CUSTOM_LABEL_OPTION, currencySymbols } from "../../helper/currencySymbols";

const ExtraConfig = ({ formik, changeTextFn }) => {
    const [autoSwitchStatus, setAutoSwitchStatus] = useState(false);
    const [removeDecimalsStatus, setRemoveDecimalsStatus] = useState(false);
    const [pricingRoundingStatus, setPricingRoundingStatus] = useState(false);
    const [cartMessage, setCartMessage] = useState('');
    const [messageColor, setMessageColor] = useState('#000000');
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');

    // Local state for the "Add" forms (country-override rules + custom formats).
    const [newRuleCountry, setNewRuleCountry] = useState('');
    const [newRuleCurrency, setNewRuleCurrency] = useState('');
    const [newFormatCurrency, setNewFormatCurrency] = useState('');
    const [newFormatPosition, setNewFormatPosition] = useState('prefix');
    // Symbol picker for the "Add format" flow. '' = auto (first known symbol
    // for the selected currency, or the currency code as fallback).
    const [newFormatSymbol, setNewFormatSymbol] = useState('');
    const [newFormatCustomLabel, setNewFormatCustomLabel] = useState('');

    // Dropdown option sources:
    //  - currencyOptions: limited to the merchant's ENABLED currencies (only
    //    those can actually be converted to at runtime via Currency.convert).
    //  - countryOptions: full ISO 3166-1 alpha-2 list. The runtime matches on
    //    the actual visitor country from Shopify's browsing_context_suggestions
    //    (e.g. "DE", "FR", "AE"), which is independent of what currencies the
    //    merchant has enabled. Using the enabled-currency countryCode field
    //    would only expose currency-region labels like "EU" that never match.
    const enabledCurrencies = formik.values.currenciesList || [];
    const currencyOptions = [
        { label: 'Select currency…', value: '' },
        ...enabledCurrencies.map((c) => ({ label: `${c.value} — ${c.label}`, value: c.value })),
    ];
    const countryOptions = [
        { label: 'Select country…', value: '' },
        ...countryList.map((c) => ({ label: `${c.code} — ${c.name}`, value: c.code })),
    ];

    const addCountryOverride = () => {
        if (!newRuleCountry || !newRuleCurrency) return;
        changeTextFn('countryOverrides', {
            ...(formik.values.countryOverrides || {}),
            [newRuleCountry]: newRuleCurrency,
        });
        setNewRuleCountry('');
        setNewRuleCurrency('');
    };
    const removeCountryOverride = (country) => {
        const next = { ...(formik.values.countryOverrides || {}) };
        delete next[country];
        changeTextFn('countryOverrides', next);
    };

    const addCustomFormat = () => {
        if (!newFormatCurrency) return;
        // Resolve the label to save:
        //  - Merchant picked "Custom label…" -> use their typed text (fallback to currency code if empty).
        //  - Merchant picked a symbol from the dropdown -> use it.
        //  - Merchant picked nothing -> use the first known symbol for this currency, else the currency code.
        let label;
        if (newFormatSymbol === CUSTOM_LABEL_OPTION) {
            label = (newFormatCustomLabel || '').trim() || newFormatCurrency;
        } else if (newFormatSymbol) {
            label = newFormatSymbol;
        } else {
            const known = currencySymbols[newFormatCurrency];
            label = (known && known[0]) || newFormatCurrency;
        }
        changeTextFn('customFormats', {
            ...(formik.values.customFormats || {}),
            [newFormatCurrency]: {
                label,
                position: newFormatPosition || 'prefix',
            },
        });
        setNewFormatCurrency('');
        setNewFormatPosition('prefix');
        setNewFormatSymbol('');
        setNewFormatCustomLabel('');
    };
    const removeCustomFormat = (code) => {
        const next = { ...(formik.values.customFormats || {}) };
        delete next[code];
        changeTextFn('customFormats', next);
    };
    const updateCustomFormat = (code, patch) => {
        changeTextFn('customFormats', {
            ...(formik.values.customFormats || {}),
            [code]: { ...(formik.values.customFormats?.[code] || {}), ...patch },
        });
    };

    const currencyLabelFor = (code) => {
        const hit = currencyListOptions.find((c) => c.value === code);
        return hit ? `${hit.value} — ${hit.label}` : code;
    };
    const countryLabelFor = (code) => `${String(code || '').toUpperCase()} — ${countryNameFor(code)}`;

    const DirhamSymbol = () => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 95 95"
          width="15"
          height="15"
          style={{
            verticalAlign: "middle",
            display: "inline-block",
          }}
        >
          <image
            width="74"
            height="64"
            href="data:image/png;base64,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"
            x="12"
            y="15"
          />
        </svg>
    );

    return (
        <>
            <div className="mt-20">
                <Layout>
                    <Layout.AnnotatedSection
                        id="pricing-rules"
                        title="Pricing Rules"
                        description={<>
                            <Text as={'span'}>Let's customize the product prices after conversion like rounding the proper digits or hiding the decimal places so that your customers will always see the cleaner prices.</Text>
                            <CurrencyPreview
                                priceFormat={formik.values?.priceFormat || 0}
                                displayFlag={formik.values?.displayFlag || 0}
                                flagStyle={formik.values?.flagStyle || 0}
                                displayBorder={formik.values?.displayBorder || 0}
                                borderColor={formik.values?.borderLineColor || '#d8d8d8'}
                                backgroundColor={formik.values?.backgroundColor || '#ffffff'}
                                backgroundHoverColor={formik.values?.hoverBackgroundColor || '#f6f6f7'}
                                textColor={formik?.values?.textColor || '#202223'}
                                textHoverColor={formik?.values?.textHoverColor || '#008060'}
                                selectedCurrencies={formik?.values?.currenciesList}
                                currencies={currencyListOptions}
                                removeDecimals={formik?.values?.removeDecimals}
                                priceRounding={formik?.values?.pricingRound}
                            />
                        </>}
                    >
                        <Card>
                            <div className="pricing-rules-extra-config justify-content-space-between">
                                <div className="extra-config-badge-div d-flex justify-content-space-between">
                                    <Text as="span" variant="span" fontWeight="medium">
                                        Geo-based currency logic
                                        <Badge tone={formik.values.ipBasedLocation ? 'success' : 'new'}>
                                            {formik.values.ipBasedLocation ? ' On' : ' Off'}
                                        </Badge>
                                    </Text>
                                    <Button
                                        variant="primary"
                                        size="micro"
                                        onClick={() => changeTextFn('ipBasedLocation', !formik.values.ipBasedLocation)}
                                    >
                                        {formik.values.ipBasedLocation ? 'Turn Off' : 'Turn On'}
                                    </Button>
                                </div>
                                <Text variant="headingSm" as={'p'} fontWeight="regular">
                                    Detects the visitor's country and applies region-specific currency rules automatically. Turn this off to disable all rules in the Advanced Currency Rules section below.
                                </Text>
                            </div>
                            <div className="mt-10">
                                <Divider borderColor="border" />
                            </div>
                            <div className="mt-10 pricing-rules-extra-config justify-content-space-between">
                                <div className="extra-config-badge-div d-flex justify-content-space-between">
                                    <Text as="span" variant="span" fontWeight="medium">
                                        Remove Decimals
                                        <Badge tone={formik.values.removeDecimals ? 'success' : 'new'}>
                                            {formik.values.removeDecimals ? ' On' : ' Off'}
                                        </Badge>
                                    </Text>
                                    <Button
                                        variant="primary"
                                        size="micro"
                                        onClick={() => changeTextFn('removeDecimals', !formik.values.removeDecimals)}
                                    >
                                        {formik.values.removeDecimals ? 'Turn Off' : 'Turn On'}
                                    </Button>
                                </div>
                                <Text variant="headingSm" as={'p'} fontWeight="regular">
                                    Customers will see prices without decimal values.
                                </Text>
                            </div>
                            <div className="mt-10">
                                <Divider borderColor="border" />
                            </div>
                            <div className="mt-10 pricing-rules-extra-config justify-content-space-between">
                                <div className="extra-config-badge-div d-flex justify-content-space-between">
                                    <Text as="span" variant="span" fontWeight="medium">
                                        Pricing Rounding Rules
                                        <Badge tone={formik.values.pricingRound ? 'success' : 'new'}>
                                            {formik.values.pricingRound ? ' On' : ' Off'}
                                        </Badge>
                                    </Text>
                                    <Button
                                        variant="primary"
                                        size="micro"
                                        onClick={() => changeTextFn('pricingRound', !formik.values.pricingRound)}
                                    >
                                        {formik.values.pricingRound ? 'Turn Off' : 'Turn On'}
                                    </Button>
                                </div>
                                <Text variant="headingSm" as={'p'} fontWeight="regular">
                                    Customers will see prices with rounded decimal values.
                                </Text>
                            </div>
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </div>
            <div className="mt-20">
                <Divider borderColor="border" />
            </div>
            <div className="mt-20">
                <Layout>
                    <Layout.AnnotatedSection
                        id="advanced-currency-rules"
                        title="Advanced Currency Rules"
                        description="Region-based currency detection and per-currency label formatting for shoppers around the world."
                    >
                        <Card>
                            {!formik.values.ipBasedLocation && (
                                <div className="mb-2">
                                    <Badge tone="warning">
                                        Turn on "Geo-based currency logic" above to activate these rules.
                                    </Badge>
                                </div>
                            )}
                            <div className="pricing-rules-extra-config">
                                <Text as="span" variant="span" fontWeight="medium">
                                    Country override rules
                                </Text>
                                <Text variant="headingSm" as={'p'} fontWeight="regular">
                                    Pick a country and the currency to show its visitors. These win over every other rule below. Use this to send European visitors to USD, override a Gulf country, or map any country to any of your enabled currencies.
                                </Text>
                                <div className="mt-10">
                                    <FormLayout>
                                        <FormLayout.Group condensed>
                                            <Select
                                                label="Country"
                                                options={countryOptions}
                                                value={newRuleCountry}
                                                onChange={setNewRuleCountry}
                                            />
                                            <Select
                                                label="Show currency"
                                                options={currencyOptions}
                                                value={newRuleCurrency}
                                                onChange={setNewRuleCurrency}
                                            />
                                        </FormLayout.Group>
                                        <div className="d-flex" style={{ justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="primary"
                                                size="slim"
                                                disabled={!newRuleCountry || !newRuleCurrency}
                                                onClick={addCountryOverride}
                                            >
                                                Add rule
                                            </Button>
                                        </div>
                                    </FormLayout>
                                </div>
                                {Object.keys(formik.values.countryOverrides || {}).length > 0 && (
                                    <div className="mt-10">
                                        <BlockStack gap="200">
                                            {Object.entries(formik.values.countryOverrides || {}).map(([country, currency]) => (
                                                <div
                                                    key={country}
                                                    className="d-flex justify-content-space-between align-items-center"
                                                    style={{ padding: '8px 12px', border: '1px solid #e1e3e5', borderRadius: 6 }}
                                                >
                                                    <Text as="span" variant="bodyMd">
                                                        <strong>{countryLabelFor(country)}</strong>
                                                        {' → '}
                                                        {currencyLabelFor(currency)}
                                                    </Text>
                                                    <Button
                                                        size="micro"
                                                        tone="critical"
                                                        onClick={() => removeCountryOverride(country)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            ))}
                                        </BlockStack>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </div>
            <div className="mt-20">
                <Divider borderColor="border" />
            </div>
            <div className="mt-20">
                <Layout>
                    <Layout.AnnotatedSection
                        id="custom-currency-formatting"
                        title="Custom currency formatting"
                        description="Pick which symbol to show for each currency (e.g. kr vs DKK for Danish Krone) and where it sits. Choose from common symbols or type your own via Custom label…"
                    >
                        <Card>
                            <FormLayout>
                                <FormLayout.Group condensed>
                                    <Select
                                        label="Currency"
                                        options={currencyOptions}
                                        value={newFormatCurrency}
                                        onChange={(v) => {
                                            setNewFormatCurrency(v);
                                            setNewFormatSymbol('');
                                            setNewFormatCustomLabel('');
                                        }}
                                    />
                                    <Select
                                        label="Symbol / label"
                                        options={newFormatCurrency
                                            ? [{ label: 'Recommended default', value: '' }, ...getSymbolOptions(newFormatCurrency)]
                                            : [{ label: 'Select a currency first', value: '' }]}
                                        value={newFormatSymbol}
                                        onChange={setNewFormatSymbol}
                                        disabled={!newFormatCurrency}
                                    />
                                    <Select
                                        label="Position"
                                        options={[
                                            { label: 'Prefix (e.g. $1,250)', value: 'prefix' },
                                            { label: 'Suffix (e.g. 1,250 SAR)', value: 'suffix' },
                                        ]}
                                        value={newFormatPosition}
                                        onChange={setNewFormatPosition}
                                    />
                                </FormLayout.Group>
                                {newFormatSymbol === CUSTOM_LABEL_OPTION && (
                                    <TextField
                                        label="Custom label"
                                        value={newFormatCustomLabel}
                                        onChange={setNewFormatCustomLabel}
                                        autoComplete="off"
                                        placeholder={`e.g. ${newFormatCurrency || 'USD'}`}
                                        helpText="This exact text will appear next to the price."
                                    />
                                )}
                                <div className="d-flex" style={{ justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="primary"
                                        size="slim"
                                        disabled={!newFormatCurrency
                                            || (newFormatSymbol === CUSTOM_LABEL_OPTION && !newFormatCustomLabel.trim())}
                                        onClick={addCustomFormat}
                                    >
                                        Add format
                                    </Button>
                                </div>
                            </FormLayout>
                            {Object.keys(formik.values.customFormats || {}).length > 0 && (
                                <div className="mt-10">
                                    <BlockStack gap="200">
                                        {Object.entries(formik.values.customFormats || {}).map(([code, entry]) => {
                                            const currentLabel = (entry && entry.label) || '';
                                            const symbolOpts = getSymbolOptions(code);
                                            const isCustomLabel = currentLabel && !symbolOpts.some((o) => o.value === currentLabel);
                                            const selectValue = isCustomLabel ? CUSTOM_LABEL_OPTION : currentLabel;
                                            return (
                                            <div
                                                key={code}
                                                style={{ padding: '8px 12px', border: '1px solid #e1e3e5', borderRadius: 6 }}
                                            >
                                                <div className="d-flex justify-content-space-between align-items-center mb-2">
                                                    <Text as="span" variant="bodyMd" fontWeight="medium">
                                                        {currencyLabelFor(code)}
                                                    </Text>
                                                    <Button
                                                        size="micro"
                                                        tone="critical"
                                                        onClick={() => removeCustomFormat(code)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                                <FormLayout.Group condensed>
                                                    <Select
                                                        label="Symbol / label"
                                                        options={symbolOpts}
                                                        value={selectValue}
                                                        onChange={(v) => {
                                                            if (v === CUSTOM_LABEL_OPTION) {
                                                                // Reveal the text input but keep the current label until they type something.
                                                                updateCustomFormat(code, { label: isCustomLabel ? currentLabel : '' });
                                                            } else {
                                                                updateCustomFormat(code, { label: v });
                                                            }
                                                        }}
                                                    />
                                                    <Select
                                                        label="Position"
                                                        options={[
                                                            { label: 'Prefix (e.g. $1,250)', value: 'prefix' },
                                                            { label: 'Suffix (e.g. 1,250 SAR)', value: 'suffix' },
                                                        ]}
                                                        value={(entry && entry.position) || 'prefix'}
                                                        onChange={(v) => updateCustomFormat(code, { position: v })}
                                                    />
                                                </FormLayout.Group>
                                                {(selectValue === CUSTOM_LABEL_OPTION) && (
                                                    <div className="mt-10">
                                                        <TextField
                                                            label="Custom label"
                                                            value={currentLabel}
                                                            onChange={(v) => updateCustomFormat(code, { label: v })}
                                                            autoComplete="off"
                                                            placeholder={`e.g. ${code}`}
                                                            helpText="This exact text will appear next to the price."
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                        })}
                                    </BlockStack>
                                </div>
                            )}
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </div>
            <div className="mt-20">
                <Divider borderColor="border" />
            </div>
            <div className="mt-20">
                <Layout.AnnotatedSection
                    id="cart-notice"
                    title="Cart Notice"
                    description="Customize the message that appears in the cart to inform customers about currency conversion and checkout process."
                >
                    <Card>
                        <FormLayout>
                            <FormLayout.Group condensed>
                                <div className="pricing-rules-extra-config justify-content-space-between">
                                    <div className="extra-config-badge-div d-flex justify-content-space-between">
                                        <Text as="span" variant="span" fontWeight="medium">
                                            Cart Message
                                            <Badge tone={formik.values.showCartNotice ? 'success' : 'new'} style={{ marginLeft: '0.5rem' }}>
                                                {formik.values.showCartNotice ? ' On' : ' Off'}
                                            </Badge>
                                        </Text>
                                        <Button
                                            variant="primary"
                                            size="micro"
                                            onClick={() => changeTextFn('showCartNotice', !formik.values.showCartNotice)}
                                        >
                                            {formik.values.showCartNotice ? 'Turn Off' : 'Turn On'}
                                        </Button>
                                    </div>
                                    <TextField
                                        value={formik.values.cartNoticeMessage}
                                        onChange={(value) => changeTextFn('cartNoticeMessage', value)}
                                        multiline={4}
                                        autoComplete="off"
                                        suffix={
                                            <div
                                                onClick={() => changeTextFn('cartNoticeMessage', formik.values.defaultCartNoticeMessage)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    cursor: "pointer",
                                                    color: "var(--p-color-text-link)"
                                                }}
                                            >
                                                <span>Set Default</span>
                                            </div>
                                        }
                                    />
                                </div>

                            </FormLayout.Group>
                            <FormLayout.Group condensed>
                                <TextField
                                    label="Message Color"
                                    value={formik.values.cartNoticetextColor}
                                    onChange={(value) => changeTextFn('cartNoticetextColor', value)}
                                    autoComplete="off"
                                    suffix={
                                        <input
                                            type="color"
                                            value={formik.values.cartNoticetextColor}
                                            onChange={(e) => changeTextFn('cartNoticetextColor', e.target.value)}
                                            className="color-input-textfield"
                                        />
                                    }
                                />
                                <TextField
                                    label="Background Color"
                                    value={formik.values.cartNoticetextBackgroundColor}
                                    onChange={(value) => changeTextFn('cartNoticetextBackgroundColor', value)}
                                    autoComplete="off"
                                    suffix={
                                        <input
                                            type="color"
                                            value={formik.values.cartNoticetextBackgroundColor}
                                            onChange={(e) => changeTextFn('cartNoticetextBackgroundColor', e.target.value)}
                                            className="color-input-textfield"
                                        />
                                    }
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card>
                </Layout.AnnotatedSection>
            </div>
            <div className="mt-20">
                <Divider borderColor="border" />
            </div>
            <div className="mt-20">
                <Layout.AnnotatedSection
                    id="currency-symbol-toggle"
                    title="Currency Display Format"
                    description={
                        <>
                          Choose whether to show the AED currency as a symbol (<DirhamSymbol />) or as the currency code (<strong>AED</strong>).
                        </>
                    }
                >
                    <Card>
                        <FormLayout>
                            <FormLayout.Group condensed>
                                <div className="d-flex justify-content-space-between align-items-center">
                                    <BlockStack gap='200'>
                                        <Text as="span" variant="span" fontWeight="medium">
                                            Show Dirham Symbol
                                            (<DirhamSymbol />)
                                            <Badge
                                                tone={formik.values.showDirhamSymbol ? "success" : "attention"}
                                                style={{ marginLeft: "0.5rem" }}
                                            >
                                                {formik.values.showDirhamSymbol ? "Enabled" : "Disabled"}
                                            </Badge>
                                        </Text>
                                        <Text variant="bodySm" as="p" tone="subdued" className="mt-2">
                                            When <strong>Enabled</strong>, the currency will display as <strong>(<DirhamSymbol />)</strong> instead of <strong>AED</strong>.
                                        </Text>
                                    </BlockStack>
                                    <Button
                                        variant="primary"
                                        tone={formik.values.showDirhamSymbol ? 'critical' : ''}
                                        size="micro"
                                        onClick={() =>
                                            changeTextFn(
                                                "showDirhamSymbol",
                                                !formik.values.showDirhamSymbol
                                            )
                                        }
                                    >
                                        {formik.values.showDirhamSymbol ? "Disable" : "Enable"}
                                    </Button>
                                </div>
                            </FormLayout.Group>
                        </FormLayout>
                    </Card>
                </Layout.AnnotatedSection>
            </div>
        </>
    );
};

export default ExtraConfig;
